import React, {
  useEffect,
  useState,
  useCallback,
  memo,
  useContext,
} from 'react';
import { Divider, Progress, Spin, Tooltip, Button, Flex, Dropdown } from 'antd';
import {
  InfoCircleOutlined,
  LoadingOutlined,
  BulbOutlined,
  BulbFilled,
} from '@ant-design/icons';
import { useRouter } from 'next/router';
import { CDN_MARKETPLACE } from '../scene/assets/baseUrl';
import Icon from '@ant-design/icons';
import Logo from '@src/theme/branding/Logo';
import NavigationButtonsGroup from './NavigationButtonsGroup';
import ProfileButtonsGroup from './ProfileButtonsGroup';
import ZepetoInstructionsModal from '../menu/zepetoInstructionsModal';
import UnityInstructionModal from '../scene/editor/unity/UnityInstructionsModal';
import { DownOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { UNITY_EXPORT } from 'util/constants';
import { ThemeContext } from 'pages/_app';
import { useBranding } from '@src/theme/branding/useBranding';
import { useSession } from 'next-auth/react';
import styles from '../navigation-bar/navigation-bar.module.css';

type Props = {
  isSignIn: boolean;
  userEmail: string;
  setIsSaveScene?: Function;
  isExportScene?: boolean;
  setIsExportScene?: Function;
  exportSceneProgress?: number;
  isLoginOpen?: boolean;
  setIsLoginOpen?: Function;
  setIsTourOpen?: Function;
  hasChanges?: boolean;
  ref1?: any;
  ref2?: any;
  ref3?: any;
  ref4?: any;
  ref5?: any;
  ref6?: any;
  ref7?: any;
  ref8?: any;
  ref9?: any;
  ref10?: any;
  ref11?: any;
  ref12?: any;
  ref13?: any;
  ref14?: any;
  ref15?: any;
  ref16?: any;
  ref17?: any;
  ref18?: any;
  isCreator?: boolean;
  externalLogoURL?: string;
};

function ExportSceneProgress({ exportSceneProgress }) {
  return (
    <Tooltip
      title={
        <Flex align='center' gap={10}>
          Do you want to cancel?{' '}
          <Button
            onClick={() =>
              document.dispatchEvent(new CustomEvent('cancel_export'))
            }
          >
            Yes
          </Button>
        </Flex>
      }
      trigger='hover'
      color={'#f50'}
    >
      <span style={{ position: 'relative', cursor: 'pointer' }}>
        <Spin
          style={{ position: 'absolute', color: '#1677ff' }}
          indicator={<LoadingOutlined style={{ fontSize: 50, top: 7 }} spin />}
        />
        <Progress type='circle' percent={exportSceneProgress} size={50} />
      </span>
    </Tooltip>
  );
}

const MemoExportSceneProgress = memo(ExportSceneProgress);

function SaveIcon() {
  return (
    <img
      src={`${CDN_MARKETPLACE}/assets/logos/black-save-icon.svg`}
      alt='icon'
      width={17}
      height={17}
      style={{ marginTop: -2 }}
    />
  );
}

// This is a custom icon for ZepetoWeb export
const SaveIconZepetoWeb = () => {
  return (
    <img
      src={`${CDN_MARKETPLACE}/assets/logos/save-icon.svg`}
      alt='icon'
      width={17}
      height={17}
      style={{ marginTop: -2 }}
    />
  );
};

function ZepetowebExportButtons({
  showZepetoInstructions,
  handleOzonePackageExport,
}) {
  const items: MenuProps['items'] = [
    {
      key: '3',
      label: (
        <span
          className={styles.dropdownList}
          onClick={handleOzonePackageExport}
        >
          <Icon component={SaveIcon} />
          <span style={{ color: '#22222' }}> UnityWeb</span>
        </span>
      ),
    },
  ];

  const menuProps = {
    items,
  };

  return (
    <div className={styles.dropdownWrapper}>
      <Dropdown menu={menuProps} className={styles.mobileSaveButtons}>
        <Button className={styles.dropdownButton}>
          <Icon component={SaveIconZepetoWeb} />
          <span style={{ color: '#fff', lineHeight: 22 }}>Export as</span>
          <DownOutlined style={{ color: '#fff' }} />
        </Button>
      </Dropdown>
      <InfoCircleOutlined
        onClick={showZepetoInstructions}
        style={{ fontSize: 20, height: 24 }}
      />
    </div>
  );
}

const MemoZepetowebExportButtons = memo(ZepetowebExportButtons);

function UnityExportButtons({
  showZepetoInstructions,
  handleGlbExport,
  handleUnityExport,
}) {
  const items: MenuProps['items'] = [
    {
      key: '4',
      label: (
        <span className={styles.dropdownList} onClick={handleGlbExport}>
          <Icon component={SaveIcon} />
          <span style={{ color: '#22222' }}> Save as .GLB</span>
        </span>
      ),
    },
    {
      key: '5',
      label: (
        <span className={styles.dropdownList} onClick={handleUnityExport}>
          <Icon component={SaveIcon} />
          <span style={{ color: '#22222' }}> Save as .unitypackage</span>
        </span>
      ),
    },
  ];

  const menuProps = {
    items,
  };

  return (
    <div className={styles.dropdownWrapper}>
      <Dropdown menu={menuProps} className={styles.mobileSaveButtons}>
        <Button className={styles.dropdownButton}>
          <Icon component={SaveIconZepetoWeb} />
          <span style={{ color: '#fff', lineHeight: 22 }}>Export as</span>
          <DownOutlined style={{ color: '#fff' }} />
        </Button>
      </Dropdown>
      <InfoCircleOutlined
        onClick={showZepetoInstructions}
        style={{ fontSize: 20, height: 24 }}
      />
    </div>
  );
}

const MemoUnityExportButtons = memo(UnityExportButtons);

export const NavigationBar: React.FC<Props> = ({
  isSignIn,
  userEmail,
  setIsSaveScene,
  isExportScene,
  setIsExportScene,
  exportSceneProgress,
  isLoginOpen = false,
  setIsLoginOpen = () => {},
  setIsTourOpen = () => {},
  hasChanges = true,
  ref1,
  ref2,
  ref3,
  ref4,
  ref5,
  ref6,
  ref7,
  ref8,
  ref9,
  ref10,
  ref11,
  ref12,
  ref13,
  ref14,
  ref15,
  ref16,
  ref17,
  ref18,
  isCreator = false,
  externalLogoURL,
}) => {
  const router = useRouter();
  const [isSaveUnityModalOpen, setIsSaveUnityModalOpen] =
    useState<boolean>(false);
  const [inEditor, setInEditor] = useState<boolean>(false);
  const [isZepetoInstructionsVisible, setZepetoInstructionsVisible] =
    useState<boolean>(false);
  const [isUnityInstructionsVisible, setUnityInstructionsVisible] =
    useState<boolean>(false);

  const { isZepeto, isUnity, isXsolla } = useBranding();
  const { isDark, toggleTheme } = useContext(ThemeContext);
  const { data: session } = useSession();

  const showZepetoInstructions = useCallback(
    () => setZepetoInstructionsVisible(true),
    []
  );

  const handleUnityExport = useCallback(() => {
    if (!setIsExportScene) {
      return;
    }
    setIsExportScene('unity');
    setIsSaveUnityModalOpen(true);
  }, [setIsExportScene]);

  const handleGlbExport = useCallback(() => {
    if (!setIsExportScene) {
      return;
    }
    setIsExportScene('glb');
    setIsSaveUnityModalOpen(true);
  }, [setIsExportScene]);

  const handleOzonePackageExport = useCallback(() => {
    if (!setIsExportScene) {
      return;
    }
    setIsExportScene('ozonepackage');
    setIsSaveUnityModalOpen(true);
  }, [setIsExportScene]);

  const sceneId = Array.isArray(router.query?.id)
    ? router.query?.id[0]
    : router.query?.id;

  useEffect(() => {
    if (
      router.pathname === '/user/studio/scene/[id]' ||
      router.pathname === '/new-template'
    ) {
      setInEditor(true);
    }
  });

  return (
    <div className={`${styles.container} ${isDark ? styles.darkTheme : ''}`}>
      <div
        style={{ height: '100%' }}
        className={inEditor ? styles.rootEditor : styles.root}
      >
        <div className={styles.left}>
          <Logo
            styles={styles}
            inEditor={inEditor}
            externalLogoURL={externalLogoURL}
            isDark={isDark}
          />
        </div>
        <div className={styles.profileCol}>
          {inEditor && (
            <div className={styles.editorButtons} id='editorButtons'>
              <NavigationButtonsGroup
                isSignIn={isSignIn}
                sceneId={sceneId}
                setIsSaveScene={setIsSaveScene}
                hasChanges={hasChanges}
                ref1={ref1}
                ref2={ref2}
                ref3={ref3}
              />
              {(session?.user?.role === 'admin' || isXsolla) && (
                <Flex align='center'>
                  <Tooltip
                    title={
                      isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'
                    }
                  >
                    <Button
                      type='text'
                      icon={isDark ? <BulbFilled /> : <BulbOutlined />}
                      onClick={toggleTheme}
                      style={{ fontSize: '20px' }}
                    />
                  </Tooltip>
                  <Divider type='vertical' className={styles.divider} />
                </Flex>
              )}
              {UNITY_EXPORT && isZepeto && (
                <Flex ref={ref4} align='center'>
                  {isExportScene ? (
                    <MemoExportSceneProgress
                      exportSceneProgress={exportSceneProgress}
                    />
                  ) : (
                    <MemoZepetowebExportButtons
                      showZepetoInstructions={showZepetoInstructions}
                      handleOzonePackageExport={handleOzonePackageExport}
                    />
                  )}
                  <Divider type='vertical' className={styles.divider} />
                </Flex>
              )}
              {UNITY_EXPORT && isUnity && (
                <Flex ref={ref4} align='center'>
                  {isExportScene ? (
                    <MemoExportSceneProgress
                      exportSceneProgress={exportSceneProgress}
                    />
                  ) : (
                    <MemoUnityExportButtons
                      showZepetoInstructions={showZepetoInstructions}
                      handleUnityExport={handleUnityExport}
                      handleGlbExport={handleGlbExport}
                    />
                  )}
                  <Divider type='vertical' className={styles.divider} />
                </Flex>
              )}
            </div>
          )}
        </div>
        <ProfileButtonsGroup
          inEditor={inEditor}
          isSaveUnityModalOpen={isSaveUnityModalOpen}
          isSignIn={isSignIn}
          styles={styles}
          userEmail={userEmail}
          isLoginOpen={isLoginOpen}
          setIsTourOpen={setIsTourOpen}
          setIsLoginOpen={setIsLoginOpen}
          setIsSaveUnityModalOpen={setIsSaveUnityModalOpen}
        />
      </div>
      {UNITY_EXPORT && isUnity && (
        <UnityInstructionModal
          isOpen={isZepetoInstructionsVisible}
          setIsOpen={setZepetoInstructionsVisible}
        />
      )}
      <ZepetoInstructionsModal
        isOpen={isUnityInstructionsVisible}
        setIsOpen={setUnityInstructionsVisible}
      />
    </div>
  );
};
